import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { KibSuperSelectOption } from '@chewy/kib-fields-new-react'
import { Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { ButtonWithLoader, type Field, Text } from '@pbt/pbt-ui-components'

import type { ConversationTransport } from '~/api/graphql/generated/types'
import ContactTargetSlot, {
  getSlotId,
} from '~/components/common/ContactTargetSlot'
import AppointmentSelect from '~/components/dashboard/communications/new-conversation-dialog/AppointmentSelect'
import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getPatientName } from '~/store/reducers/patients'
import type { ContactSlot } from '~/types'

import ConversationTransportSelect from './ConversationTransportSelect'

const useStyles = makeStyles(
  (theme) => ({
    content: {
      minHeight: 240,
    },
    addButton: {
      width: 150,
    },
    buttonsSection: {
      borderTop: theme.constants.tableBorder,
    },
  }),
  { name: 'NewConversation' },
)

const isPayloadSlot = (contactSlot: ContactSlot) =>
  Boolean(contactSlot.contactId) || Boolean(contactSlot.clientId)

export interface NewConversationProps {
  allAppointments: KibSuperSelectOption[]
  appointmentField: Field
  contactSlot: ContactSlot
  handleAddConversation: () => void
  isLoading?: boolean
  setContactSlot: (contactSlot: ContactSlot) => void
  setTransport: (transport: ConversationTransport) => void
  transport?: ConversationTransport
}

const NewConversation = ({
  isLoading,
  transport,
  setTransport,
  handleAddConversation,
  contactSlot,
  setContactSlot,
  appointmentField,
  allAppointments,
}: NewConversationProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Dialogs'])
  const isConversationDialogAppointmentDropdownEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CONVERSATION_DIALOG_APPOINTMENT_DROPDOWN),
  )
  const patientName = useSelector(getPatientName(contactSlot.patientId)) || ''

  const rootRef = useRef<HTMLDivElement>(null)

  const isContactChosen = isPayloadSlot(contactSlot)

  return (
    <Grid container item direction="column" ref={rootRef}>
      <Grid container className={classes.content} direction="column" px={2}>
        <Grid container item>
          <Text strong mb={2} pb={1} pt={2} variant="body2">
            {t('Dialogs:NEW_CONVERSATION_WITH_STEPS_DIALOG.SEARCH_TITLE')}
          </Text>
          <ContactTargetSlot
            showAddClient
            anchorRef={rootRef}
            key={getSlotId(contactSlot)}
            visibleResults={6}
            onSlotChange={(newContactSlot) => setContactSlot(newContactSlot)}
            {...contactSlot}
          />
        </Grid>
        <Grid mt={2}>
          {isContactChosen && (
            <>
              {isConversationDialogAppointmentDropdownEnabled && (
                <Grid container item mb={2}>
                  <AppointmentSelect
                    appointmentField={appointmentField}
                    appointments={allAppointments}
                    patientName={patientName}
                  />
                </Grid>
              )}
              <ConversationTransportSelect
                contactSlot={contactSlot}
                label={t(
                  'Dialogs:NEW_CONVERSATION_WITH_STEPS_DIALOG.TYPE_CONVERSATION',
                )}
                transport={transport}
                onChange={setTransport}
              />
            </>
          )}
        </Grid>
      </Grid>
      <Grid container className={classes.buttonsSection} p={2}>
        <ButtonWithLoader
          className={classes.addButton}
          disabled={isLoading || !isContactChosen || !transport}
          loading={isLoading}
          onClick={handleAddConversation}
        >
          {t('Common:ADD_ACTION')}
        </ButtonWithLoader>
      </Grid>
    </Grid>
  )
}

export default NewConversation
